import React from 'react';
import { Modal, Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import * as FaIcons from 'react-icons/fa';

/**
 * Modal genérico para CRUD de mantenedores generales
 * Maneja creación, edición y visualización de elementos
 */
const GeneralesModal = ({
  show,
  onHide,
  mode, // 'view', 'create', 'edit'
  title,
  item,
  campos,
  formData,
  formErrors,
  dependentData,
  submitting,
  onFormChange,
  onSubmit,
  onEdit,
  onDelete,
  permisos,
  modelo // Agregamos el modelo para identificar el tipo
}) => {
  
  // Configurar props del modal según el modo
  const getModalProps = () => {
    switch (mode) {
      case 'create':
        return {
          title: `Crear ${title}`,
          icon: <FaIcons.FaPlus className="me-2" style={{ color: '#28a745' }} />,
          variant: 'success'
        };
      case 'edit':
        return {
          title: `Editar ${title}`,
          icon: <FaIcons.FaEdit className="me-2" style={{ color: '#ffc107' }} />,
          variant: 'warning'
        };
      default: // view
        return {
          title: `Ver ${title}`,
          icon: <FaIcons.FaEye className="me-2" style={{ color: '#17a2b8' }} />,
          variant: 'info'
        };
    }
  };

  const modalProps = getModalProps();

  /**
   * Renderizar campo del formulario según su tipo
   */
  const renderField = (campo) => {
    const isReadOnly = mode === 'view';
    const value = mode === 'view' ? (item?.[campo.name] || '') : (formData[campo.name] || '');
    const hasError = formErrors[campo.name];

    // Verificar si es TMS en modo edición y tiene valores protegidos
    const isTmsProtected = mode === 'edit' && 
                          modelo === 'tms' && 
                          campo.name === 'tms' && 
                          (value === 'DispatchTrack' || value === 'GeoSort');

    // El campo está deshabilitado si es readOnly, está enviando, o es un TMS protegido
    const isFieldDisabled = isReadOnly || submitting || isTmsProtected;

    switch (campo.type) {
      case 'select':
        return (
          <Form.Select
            value={value}
            onChange={(e) => !isFieldDisabled && onFormChange(campo.name, e.target.value)}
            disabled={isFieldDisabled}
            isInvalid={hasError}
            required={campo.required}
          >
            <option value="">Seleccione...</option>
            {getSelectOptions(campo).map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        );
      
      case 'number':
        return (
          <Form.Control
            type="number"
            value={value}
            onChange={(e) => !isFieldDisabled && onFormChange(campo.name, e.target.value)}
            disabled={isFieldDisabled}
            isInvalid={hasError}
            required={campo.required}
            min={campo.min}
            max={campo.max}
            step={campo.step || 'any'}
            placeholder={`Ingrese ${campo.label.toLowerCase()}`}
          />
        );
      
      default: // text
        return (
          <div>
            <Form.Control
              type="text"
              value={value}
              onChange={(e) => !isFieldDisabled && onFormChange(campo.name, e.target.value)}
              disabled={isFieldDisabled}
              isInvalid={hasError}
              required={campo.required}
              placeholder={`Ingrese ${campo.label.toLowerCase()}`}
            />
            {isTmsProtected && (
              <small className="text-muted mt-1 d-block">
                <em>Este TMS no puede ser editado por ser un sistema protegido.</em>
              </small>
            )}
          </div>
        );
    }
  };

  /**
   * Obtener opciones para campos select
   */
  const getSelectOptions = (campo) => {
    if (!campo.options || !dependentData[campo.options]) {
      return [];
    }

    const data = dependentData[campo.options];
    
    switch (campo.options) {
      case 'regiones':
        return data.map(region => ({
          value: region.id,
          label: region.region
        }));
      
      case 'comunas':
        return data.map(comuna => ({
          value: comuna.id,
          label: `${comuna.comuna} (${comuna.region?.region || 'Sin región'})`
        }));

      case 'clientes':
        return data.map(cliente => ({
          value: cliente.id,
          label: cliente.cliente
        }));
      
      default:
        return [];
    }
  };

  /**
   * Manejar envío del formulario
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit();
    }
  };

  /**
   * Renderizar botones del footer según el modo
   */
  const renderFooterButtons = () => {
    if (mode === 'view') {
      return (
        <>
          {permisos.canEdit && (
            <Button
              variant="warning"
              onClick={() => onEdit && onEdit(item)}
              disabled={submitting}
            >
              <FaIcons.FaEdit className="me-2" />
              Editar
            </Button>
          )}
          {permisos.canDelete && (
            <Button
              variant="danger"
              onClick={() => onDelete && onDelete(item)}
              disabled={submitting}
              className="ms-2"
            >
              <FaIcons.FaTrash className="me-2" />
              Eliminar
            </Button>
          )}
          <Button variant="secondary" onClick={onHide} className="ms-2">
            Cerrar
          </Button>
        </>
      );
    }

    if (mode === 'create' || mode === 'edit') {
      return (
        <>
          <Button
            type="submit"
            variant={modalProps.variant}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                {mode === 'create' ? 'Creando...' : 'Actualizando...'}
              </>
            ) : (
              <>
                {mode === 'create' ? (
                  <FaIcons.FaPlus className="me-2" />
                ) : (
                  <FaIcons.FaSave className="me-2" />
                )}
                {mode === 'create' ? 'Crear' : 'Guardar'}
              </>
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={submitting}
            className="ms-2"
          >
            Cancelar
          </Button>
        </>
      );
    }

    return null;
  };

  /**
   * Renderizar información adicional para modo vista
   */
  const renderViewInfo = () => {
    if (mode !== 'view' || !item) return null;

    return (
      <Row className="mb-3">
        <Col>
          <Alert variant="light" className="mb-0">
            <Row>
              <Col md={6}>
                <small className="text-muted">
                  <strong>ID:</strong> {item.id}
                </small>
              </Col>
              {item.created_at && (
                <Col md={6}>
                  <small className="text-muted">
                    <strong>Creado:</strong> {new Date(item.created_at).toLocaleString('es-CL')}
                  </small>
                </Col>
              )}
              {item.updated_at && (
                <Col md={6}>
                  <small className="text-muted">
                    <strong>Actualizado:</strong> {new Date(item.updated_at).toLocaleString('es-CL')}
                  </small>
                </Col>
              )}
            </Row>
          </Alert>
        </Col>
      </Row>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalProps.icon}
            {modalProps.title}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {renderViewInfo()}
          
          <Row>
            {campos.map((campo) => (
              <Col md={campo.type === 'select' ? 12 : 6} key={campo.name} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    {campo.label}
                    {campo.required && <span className="text-danger ms-1">*</span>}
                  </Form.Label>
                  
                  {renderField(campo)}
                  
                  {formErrors[campo.name] && (
                    <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                      {formErrors[campo.name]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            ))}
          </Row>
          
          {/* Mostrar campos requeridos solo en modo creación/edición */}
          {(mode === 'create' || mode === 'edit') && (
            <Row>
              <Col>
                <small className="text-muted">
                  <FaIcons.FaAsterisk size={8} className="text-danger me-1" />
                  Campos obligatorios
                </small>
              </Col>
            </Row>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          {renderFooterButtons()}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default GeneralesModal;
